"use client"

import { usePathname, useSearchParams } from "next/navigation"
import { useEffect, useState } from "react"
import type { HttpTypes } from "@medusajs/types"
import posthog from "posthog-js"
import {
  captureAnalyticsEvent,
  capturePageView,
  persistSourceChannel,
  syncAnalyticsContext,
  syncPostHogIdentity,
} from "@/lib/analytics/posthog-client"

/** IPs that should not be tracked (e.g. internal/dev). */
const OPT_OUT_IPS = ["77.38.64.205"]

const CART_ABANDONED_FIRED_KEY = "posthog_cart_abandoned_fired"

function applyPostHogIpOptOut(clientIp?: string | null) {
  if (!posthog.__loaded || !clientIp) {
    return
  }
  if (OPT_OUT_IPS.includes(clientIp)) {
    posthog.opt_out_capturing()
  }
}

function isOnCheckout(pathname: string, searchParams: URLSearchParams | null): boolean {
  if (!pathname) return false
  if (pathname.includes("/checkout")) return true
  if (pathname.includes("/products") && searchParams?.get("express") === "true") return true
  return false
}

/**
 * Initializes PostHog, manages identity, and captures route-level analytics.
 */
export function PostHogPageView({
  clientIp: initialClientIp = null,
  customer: initialCustomer = null,
}: {
  clientIp?: string | null
  customer?: Pick<HttpTypes.StoreCustomer, "id" | "email"> | null
} = {}) {
  const pathname = usePathname()
  const searchParams = useSearchParams()

  // IP + customer are fetched client-side (via /api/client-ip and /api/me) so
  // the root layout no longer reads headers()/cookies during SSR — that was
  // forcing every page to render dynamically (`no-store`, no bfcache). Props
  // are kept only as an optional initial value for backwards compatibility.
  const [clientIp, setClientIp] = useState<string | null>(initialClientIp)
  const [customer, setCustomer] = useState<
    Pick<HttpTypes.StoreCustomer, "id" | "email"> | null
  >(initialCustomer)

  useEffect(() => {
    let cancelled = false
    fetch("/api/client-ip")
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!cancelled && d?.ip) setClientIp(d.ip)
      })
      .catch(() => {})
    fetch("/api/me")
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!cancelled) setCustomer(d?.customer ?? null)
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [])

  // Initialize PostHog once. Guard against duplicate init (React Strict Mode remount,
  // HMR): a second init aborts in-flight requests and surfaces AbortError in the console.
  useEffect(() => {
    const key = process.env.NEXT_PUBLIC_POSTHOG_KEY
    const host = process.env.NEXT_PUBLIC_POSTHOG_HOST

    if (typeof window === "undefined" || !key || !host) {
      if (typeof window !== "undefined" && process.env.NODE_ENV === "development") {
        console.warn("[PostHog] Not initialized: missing NEXT_PUBLIC_POSTHOG_KEY or NEXT_PUBLIC_POSTHOG_HOST")
      }
      return
    }

    if (posthog.__loaded) {
      applyPostHogIpOptOut(clientIp)
      return
    }

    posthog.init(key, {
      api_host: host,
      ui_host: "https://eu.posthog.com",
      defaults: "2026-01-30" as "2025-05-24",
      person_profiles: "identified_only",
      capture_pageview: false,
      // Don't pull the surveys / dead-clicks remote bundles on the storefront —
      // they're unused here and show up in Lighthouse unused-JS, legacy-JS and
      // cache-lifetime audits. Re-enable if you start running PostHog surveys.
      disable_surveys: true,
      capture_dead_clicks: false,
      debug: process.env.NODE_ENV === "development",
      loaded: () => {
        applyPostHogIpOptOut(clientIp)
      },
    })
  }, [clientIp])

  useEffect(() => {
    if (!pathname || typeof window === "undefined" || !posthog.__loaded) {
      return
    }

    const sourceChannel = searchParams?.get("utm_source") ?? null

    persistSourceChannel(sourceChannel)
    syncAnalyticsContext({
      pathname,
      customer,
      sourceChannel,
    })
    capturePageView(pathname)
  }, [customer, pathname, searchParams])

  useEffect(() => {
    if (!posthog.__loaded) {
      return
    }

    syncPostHogIdentity(customer)
  }, [customer])

  useEffect(() => {
    if (typeof window === "undefined" || !("PerformanceObserver" in window)) {
      return
    }

    let largestContentfulPaintTracked = false

    try {
      const observer = new PerformanceObserver((entryList) => {
        const entries = entryList.getEntries()
        const lastEntry = entries[entries.length - 1]

        if (!lastEntry || largestContentfulPaintTracked) {
          return
        }

        const largestContentfulPaintMs = Math.round(lastEntry.startTime)

        if (largestContentfulPaintMs <= 3000) {
          return
        }

        largestContentfulPaintTracked = true

        captureAnalyticsEvent("page_load_slow", {
          lcp_ms: largestContentfulPaintMs,
        })
      })

      observer.observe({ entryTypes: ["largest-contentful-paint"] })

      return () => observer.disconnect()
    } catch {
      return
    }
  }, [])

  useEffect(() => {
    if (typeof window === "undefined" || !document.body) {
      return
    }

    const handlePotentialDeadClick = (event: MouseEvent) => {
      const target = event.target instanceof Element
        ? event.target.closest<HTMLElement>('button,[role="button"],input[type="submit"]')
        : null

      if (!target || target.hasAttribute("disabled")) {
        return
      }

      const currentUrl = window.location.href
      let observedMutation = false
      const observer = new MutationObserver(() => {
        observedMutation = true
      })

      observer.observe(document.body, {
        subtree: true,
        childList: true,
        attributes: true,
      })

      window.setTimeout(() => {
        observer.disconnect()

        if (observedMutation || window.location.href !== currentUrl) {
          return
        }

        captureAnalyticsEvent("dead_click", {
          element_tag: target.tagName.toLowerCase(),
          element_text: target.innerText?.trim().slice(0, 80) || null,
          element_role: target.getAttribute("role"),
        })
      }, 1200)
    }

    document.addEventListener("click", handlePotentialDeadClick, true)

    return () => {
      document.removeEventListener("click", handlePotentialDeadClick, true)
    }
  }, [])

  // Cart abandonment: fire when user leaves checkout (tab hidden/closed) without completing
  useEffect(() => {
    if (typeof window === "undefined" || !posthog.__loaded) {
      return
    }

    // Clear fired flag when user reaches order confirmation
    if (pathname?.includes("/order/confirmed")) {
      window.sessionStorage.removeItem(CART_ABANDONED_FIRED_KEY)
      return
    }

    if (!isOnCheckout(pathname ?? "", searchParams)) {
      return
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState !== "hidden") return
      if (window.sessionStorage.getItem(CART_ABANDONED_FIRED_KEY)) return

      captureAnalyticsEvent("cart_abandoned", {
        checkout_type: pathname?.includes("/checkout") ? "standard" : "express",
      })
      window.sessionStorage.setItem(CART_ABANDONED_FIRED_KEY, "1")
    }

    document.addEventListener("visibilitychange", handleVisibilityChange)

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange)
    }
  }, [pathname, searchParams])

  return null
}
